/* Created by Interactive 63 - https://interactive63.com */
/**
 * Editing the colour stops of gradients in the artwork itself: removing a stop, adding one between
 * two others, and moving a stop along its gradient. Stops are not layers, so these work on the SVG
 * source and the element table together, like the layer operations do.
 */
import { interpolateColour } from '../colour';
import { elementIdFromIndex } from '../model/ids';
import type { ElementId, ElementMeta, Project } from '../model/types';

/** A gradient keeps at least this many stops, since one stop is just a flat colour. */
const MIN_STOPS = 2;

/** Parses the stored source into a document. */
function parse(source: string): Document {
  return new DOMParser().parseFromString(source, 'image/svg+xml');
}

/** The stop elements that share a gradient with this one, in document order. */
function siblingStops(node: Element): Element[] {
  return Array.from(node.parentElement?.children ?? []).filter((el) => el.localName === 'stop');
}

/** A stop's value for a presentation property, from its attribute or inline style. */
function stopProperty(node: Element, name: string): string | null {
  const attribute = node.getAttribute(name);
  if (attribute !== null) return attribute;
  const style = node.getAttribute('style') ?? '';
  const match = new RegExp(`(?:^|;)\\s*${name}\\s*:\\s*([^;]+)`).exec(style);
  return match?.[1]?.trim() ?? null;
}

/** A stop's offset along its gradient, as a number from 0 to 1. Percentages are accepted. */
function offsetOf(node: Element): number {
  const raw = node.getAttribute('offset') ?? '0';
  const value = raw.trim().endsWith('%') ? parseFloat(raw) / 100 : parseFloat(raw);
  return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0;
}

/** The highest element number in use. */
function highestIndex(elements: Record<ElementId, ElementMeta>): number {
  return Object.keys(elements).reduce((top, id) => Math.max(top, Number(id.slice(1))), 0);
}

/** The offset of a stop as a percentage, or null when it is not in the artwork. */
export function readStopOffset(source: string, id: ElementId): number | null {
  const node = parse(source).querySelector(`[data-st-id="${id}"]`);
  return node ? Math.round(offsetOf(node) * 1000) / 10 : null;
}

/** The lowest and highest percentage a stop can take without passing its neighbours. */
export function stopOffsetRange(source: string, id: ElementId): { min: number; max: number } {
  const node = parse(source).querySelector(`[data-st-id="${id}"]`);
  if (!node) return { min: 0, max: 100 };
  const stops = siblingStops(node);
  const at = stops.indexOf(node);
  const before = stops[at - 1];
  const after = stops[at + 1];
  return {
    min: before ? Math.round(offsetOf(before) * 1000) / 10 : 0,
    max: after ? Math.round(offsetOf(after) * 1000) / 10 : 100,
  };
}

/** Moves a stop along its gradient, keeping it between its neighbours. Returns whether it changed. */
export function setStopOffset(draft: Project, id: ElementId, percent: number): boolean {
  const doc = parse(draft.svg.source);
  const node = doc.querySelector(`[data-st-id="${id}"]`);
  if (!node || node.localName !== 'stop' || !Number.isFinite(percent)) return false;
  const range = stopOffsetRange(draft.svg.source, id);
  const clamped = Math.min(range.max, Math.max(range.min, percent));
  node.setAttribute('offset', String(Math.round(clamped * 10) / 1000));
  const style = node.getAttribute('style');
  if (style !== null) {
    const rest = style
      .split(';')
      .filter((part) => part.split(':')[0]?.trim() !== 'offset')
      .join(';')
      .trim();
    if (rest === '') node.removeAttribute('style');
    else node.setAttribute('style', rest);
  }
  draft.svg.source = new XMLSerializer().serializeToString(doc.documentElement);
  return true;
}

export interface StopDeleteResult {
  removed: ElementId[];
  /** Why some or all stops were kept, in plain language. */
  reason?: string;
}

/**
 * Removes stops, with any animation they have. A gradient always keeps at least two stops, so a
 * stop that would leave it with fewer is kept and the reason is returned.
 */
export function deleteStops(draft: Project, ids: readonly ElementId[]): StopDeleteResult {
  const doc = parse(draft.svg.source);
  const removed: ElementId[] = [];
  let reason: string | undefined;
  for (const id of ids) {
    const node = doc.querySelector(`[data-st-id="${id}"]`);
    if (!node || node.localName !== 'stop') continue;
    if (siblingStops(node).length <= MIN_STOPS) {
      reason ??= 'A gradient needs at least two colour stops, so this one was kept.';
      continue;
    }
    node.remove();
    removed.push(id);
  }
  if (removed.length === 0) return { removed, ...(reason ? { reason } : {}) };
  draft.svg.source = new XMLSerializer().serializeToString(doc.documentElement);
  const gone = new Set(removed);
  draft.tracks = draft.tracks.filter((track) => !gone.has(track.elementId));
  draft.elements = Object.fromEntries(
    Object.entries(draft.elements).filter(([id]) => !gone.has(id)),
  );
  return { removed, ...(reason ? { reason } : {}) };
}

/**
 * Adds a stop after another, halfway to the next stop (or just beyond the last) in a colour
 * between them, so the gradient looks the same until the new stop is edited. Returns its ID.
 */
export function addStopAfter(draft: Project, id: ElementId): ElementId | null {
  const doc = parse(draft.svg.source);
  const node = doc.querySelector(`[data-st-id="${id}"]`);
  const meta = draft.elements[id];
  if (!node || node.localName !== 'stop' || !meta) return null;
  const next = siblingStops(node)[siblingStops(node).indexOf(node) + 1];
  const here = offsetOf(node);
  const offset = next ? (here + offsetOf(next)) / 2 : here + (1 - here) / 2;

  const copy = node.cloneNode(false) as Element;
  const newId = elementIdFromIndex(highestIndex(draft.elements) + 1);
  copy.setAttribute('data-st-id', newId);
  copy.removeAttribute('id');
  copy.setAttribute('offset', String(Math.round(offset * 1000) / 1000));
  const colour = stopProperty(node, 'stop-color');
  const nextColour = next ? stopProperty(next, 'stop-color') : null;
  if (colour && nextColour) {
    try {
      copy.setAttribute('stop-color', interpolateColour(colour, nextColour, 0.5));
    } catch {
      // A colour the editor cannot mix keeps the colour of the stop it was copied from.
    }
  }
  node.after(copy);
  draft.svg.source = new XMLSerializer().serializeToString(doc.documentElement);

  const gradientName = /\(([^)]*)\)\s*$/.exec(meta.label)?.[1] ?? 'gradient';
  const number = siblingStops(copy).length;
  draft.elements[newId] = {
    id: newId,
    tag: 'stop',
    label: `Stop ${number} (${gradientName})`,
    parentId: null,
    childIds: [],
    origin: { x: 50, y: 50 },
    excludeFromExport: false,
    hiddenInEditor: false,
  };
  return newId;
}
